import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  ContextualMenu,
  IconEllipsisVertical,
  MenuItem,
  MenuList,
  useTheme
} from '@veneer/core';

import { useDependencyManagerContext } from 'src/contexts/dependencyManager';
import { OperationStatus } from 'src/types';
import { Device } from 'src/types/devices';
import {
  DeviceTelemetryStatus,
  PostDeviceTelemetryPayload,
  DeviceTelemetryRequest,
  UserType
} from 'src/types/device-telemetry';
import { RootState } from 'src/store';
import {
  postDeviceTelemetryCleanup,
  postDeviceTelemetryRequest
} from 'src/features/telemetry/device-telemetry-slice';
import usePostTelemetryToast from 'src/hooks/usePostTelemetryToast';

export type ActionMenuProps = {
  deviceId: string;
  device: Device;
  isExpired: boolean;
};

const ActionMenu: React.FC<ActionMenuProps> = (props: ActionMenuProps) => {
  const { deviceId, device, isExpired } = props;
  const {
    translate,
    northboundAPIs: { v1 }
  } = useDependencyManagerContext();
  const theme = useTheme();
  const dispatch = useDispatch();
  const [isDataRequested, setIsDataRequested] = useState(false);

  const { deviceTelemetry, postDeviceTelemetry, getDeviceTelemetry } =
    useSelector((state: RootState) => ({
      deviceTelemetry:
        state.DeviceTelemetryReducer?.deviceTelemetry?.[device.device_type]?.[
          device.device_serial_nr
        ] ?? {},
      postDeviceTelemetry: state.DeviceTelemetryReducer?.postDeviceTelemetry?.[
        device.device_serial_nr
      ] || { status: OperationStatus.NOT_STARTED },
      getDeviceTelemetry: state.DeviceTelemetryReducer?.getDeviceTelemetry || {
        status: OperationStatus.NOT_STARTED
      }
    }));

  usePostTelemetryToast(postDeviceTelemetry, device);

  useEffect(() => {
    return () => {
      dispatch(postDeviceTelemetryCleanup());
    };
  }, [dispatch]);

  useEffect(() => {
    if (isDataRequested) {
      if (getDeviceTelemetry.status === OperationStatus.COMPLETED) {
        setIsDataRequested(false);
      }
      return;
    }
    if (postDeviceTelemetry.status === OperationStatus.COMPLETED) {
      setIsDataRequested(true);
    }
  }, [postDeviceTelemetry, getDeviceTelemetry]);

  const { hasCompletedTelemetry, hasPendingTelemetry } = useMemo(() => {
    const queuedRequests = deviceTelemetry[DeviceTelemetryStatus.QUEUED];
    const createdRequests = deviceTelemetry[DeviceTelemetryStatus.CREATED];
    return {
      hasCompletedTelemetry:
        (deviceTelemetry[DeviceTelemetryStatus.COMPLETED]?.length ?? 0) > 0,
      hasPendingTelemetry:
        (createdRequests?.length ?? 0) > 0 || (queuedRequests?.length ?? 0) > 0
    };
  }, [deviceTelemetry]);

  const isAlreadyRequested = hasPendingTelemetry || isDataRequested;
  const isRequestDisabled = isExpired || isAlreadyRequested;

  const handleRequestData = (): void => {
    const telemetryPayload: PostDeviceTelemetryPayload = {
      device: {
        device_type: device.device_type,
        device_sub_type: device.device_sub_type,
        device_serial_nr: device.device_serial_nr,
        device_uuid: device.device_uuid,
        device_model: device.device_model,
        device_family: device.device_family,
        device_telemetry_format: device.device_telemetry_format
      }
    };
    dispatch(postDeviceTelemetryRequest(telemetryPayload));
  };

  const handleDownloadData = (): void => {
    const completedRequests = deviceTelemetry[DeviceTelemetryStatus.COMPLETED];
    if (!completedRequests || completedRequests.length === 0) return;
    const requestIds = completedRequests.map(
      (request: DeviceTelemetryRequest) => ({
        id: request.id,
        dateRequested: request.created_at
      })
    );
    v1.navigation.push('/terms-and-conditions', {
      requestIds,
      userType: UserType.THIRD_PARTY
    });
  };

  const unknownDeviceName = translate('components.device-item.no-data.unknown');

  const { requestAnalyticsAttributes, downloadAnalyticsAttributes } = useMemo(
    () => ({
      requestAnalyticsAttributes: {
        'data-gtm-category': 'linkClick',
        'data-gtm-id': 'shared-with-me-request-data',
        'data-gtm-value': 'request-shared-device-data'
      },
      downloadAnalyticsAttributes: {
        'data-gtm-category': 'linkClick',
        'data-gtm-id': 'shared-with-me-download-data',
        'data-gtm-value': 'download-shared-device-data'
      }
    }),
    []
  );

  return (
    <ContextualMenu
      placement="bottom-end"
      anchorNode={
        <Button
          appearance="ghost"
          small
          leadingIcon={<IconEllipsisVertical />}
          customStyle={{ color: theme.color.foreground.medium }}
          aria-label={translate(
            'components.shared-with-me-table.action-menu.aria-label',
            {
              deviceFamily: device.device_family || unknownDeviceName,
              serialNumber: device.device_serial_nr
            }
          )}
          data-testid={`react-device-management-mfe-shared-with-me-table-action-menu-${deviceId}`}
        />
      }
    >
      <MenuList>
        {getDeviceTelemetry.status === OperationStatus.RUNNING ? (
          <div data-testid="react-device-management-mfe-shared-with-me-table-action-menu-loading">
            <Button
              appearance="ghost"
              loading
              disabled
              aria-label={translate(
                'components.shared-with-me-table.action-menu.loading-aria-label'
              )}
            />
          </div>
        ) : (
          <MenuItem
            label={
              isAlreadyRequested
                ? translate(
                    'components.shared-with-me-table.actions.data-requested'
                  )
                : translate(
                    'components.shared-with-me-table.actions.request-data'
                  )
            }
            value=""
            disabled={isRequestDisabled}
            onClick={handleRequestData}
            {...requestAnalyticsAttributes}
          />
        )}
        {hasCompletedTelemetry && (
          <MenuItem
            label={translate(
              'components.shared-with-me-table.actions.download-data'
            )}
            value=""
            customStyle={{
              '& > *:first-of-type': { color: theme.color.primary.base.default }
            }}
            onClick={handleDownloadData}
            {...downloadAnalyticsAttributes}
          />
        )}
      </MenuList>
    </ContextualMenu>
  );
};

export default ActionMenu;
